
import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { User } from '../../../models/users.models';
import { SystemService } from 'src/app/system.service';
import { ContactReferenceService } from 'src/app/contact-reference.service';
import { AuthService } from './auth.service';
import { DocsWhitelistService } from './docs-whiltelist.service';
import { UserService } from './user.service';
import { LeasePGService } from './lease-pg.service';
import { UnitService } from './unit.service';
import { isSoaAllowedUrl } from 'models/contact-functions.enums';
import { getKeycloakContext } from './auth/auth.utils';
import { DynamicKeycloakService } from './dynamic-keycloak.service';
import { KC_CONTEXT_KEY } from './auth/auth.types';
import { AuthorizationService } from './authorization.service';
import { isVendorRegistrationIncomplete } from './vendors/vendor-register/vendor-registration-routing';

@Injectable( {
	providedIn: 'root'
} )
export class RouteGuardService  {
	ss = inject( SystemService );
	private userService = inject( UserService );
	private router = inject( Router );
	private wl = inject( DocsWhitelistService );
	private authService = inject( AuthService );
	private contactReferenceService = inject( ContactReferenceService );
	private leaseService = inject( LeasePGService );
	private dynamicKeycloakService = inject( DynamicKeycloakService );
	private unitService = inject( UnitService );
	private authorizationService = inject( AuthorizationService );

	public currentUser: User;
	public notAllowedOnDocuments = false;
	public epcexternalPartyContact = false;
	public soaViewerContact = false;
	public lawyerUser = false;

	private hasUnitsCache: { [contactId: string]: boolean } = {};


	async canActivate ( route: ActivatedRouteSnapshot, state: RouterStateSnapshot ): Promise<boolean | UrlTree> {
		const url: string = this.authService.redirectUrl || state.url;
		if ( !this.authService.redirectUrl ) {
			this.authService.redirectUrl = url;
		}
		if( !this.authService.user ) {
			const keycloackContext = getKeycloakContext()

			if( keycloackContext && keycloackContext?.authProvider === 'keycloak' ) {
				if ( await this.dynamicKeycloakService.isLoggedIn() ) {
				const data = await this.authService.retrieveActorDetails();
			
				if ( data ) {
					// remove from session storage and store in localStorage
					sessionStorage.removeItem( KC_CONTEXT_KEY );
					localStorage.setItem( KC_CONTEXT_KEY, JSON.stringify( keycloackContext ) )
		
				}
				}
			}
		}
		let isActivate: any = true;
		const contactUrlSegments = [ 'welcome-customer', 'my-units', 'my-requests', 'oc-tenders', 'cx',
			'community-wall', 'helpline-faq', 'event-calendar', 'community-documents', 'profile', 'contact-us' ];
		const vendorUrlSegments = [ 'vendor-home', 'vendor-register', 'profile' ];
		const externalURLs = [ 'visitors', 'dashboard-bi' ]
		// Empty for a child route at path '' (e.g. the /administration home page)
		const firstPathSegment = route.url?.[0]?.path ?? '';

		if ( this.ss.currentUser === undefined && !this.userService.user.getValue() ) {
			try {
				if ( firstPathSegment === 'forgot-password' || firstPathSegment === 'reset-password' ) {
					return isActivate = true;
				}
				this.currentUser = await this.userService.currentUser();
			} catch {
				this.router.navigate( [ '/signin' ] );
				return isActivate = false;
			}
		}
		let checkURL = contactUrlSegments.find( u => u === firstPathSegment );
		const actorType = this.authService?.user?.actorType;
		let isDraft = false;
		if ( actorType === 'contact' ) {
			const refs = await this.contactReferenceService.getContactReferenceByContactWithCache( this.authService?.user?.contact );
			const draftContact = this.authService?.user?.contact;
			isDraft = draftContact
				? ( await this.leaseService.isDraftLease( draftContact ) ).isDraft
				: false;
			// for (const ref of refs) {
			// 	if(ref.function === 'tenant'){
			// 		if(isDraft === false) break;
			// 	}
			// }
			if ( ( actorType && !checkURL ) || ( !actorType && checkURL ) ) {
				if ( checkURL === 'profile' ) return isActivate = true;
				checkURL = '';
				this.router.navigate( [ '/signin' ] );
				return isActivate = false;
			}
				else {
				const isTenantUser = this.ss.currentUser?.id?.includes( 'contact-' )
					? !!refs?.find( ref => ref.function === 'tenant' ) : false;
				if ( ( firstPathSegment !== 'my-units' && isTenantUser && isDraft ) ||
					( firstPathSegment === 'my-units' && isTenantUser && isDraft && !route?.children[0].routeConfig.path.includes( 'offer-to-lease' ) )
					) {
					this.router.navigate( [ '/signin' ] );
					return isActivate = false;
				}
				const unitRestrictedPaths = [ '/my-requests/booking-request', '/my-requests/general-request', '/my-requests/maintenance' ];
				if ( unitRestrictedPaths.some( p => state.url.startsWith( p ) ) ) {
					const contactId = this.authService?.user?.contact;
					if ( !( contactId in this.hasUnitsCache ) ) {
						const units = await this.unitService.getMyUnitsByEmail();
						this.hasUnitsCache[ contactId ] = !!( units && units.length > 0 );
					}
					if ( !this.hasUnitsCache[ contactId ] ) {
						return this.router.createUrlTree( [ '/welcome-customer' ] );
					}
				}
			}
		} else if ( actorType === 'vendor' ) {
			if ( isVendorRegistrationIncomplete( this.authService.user ) && firstPathSegment !== 'vendor-register' ) {
				return this.router.createUrlTree( [ '/vendor-register/registration' ] );
			}
			let checkVendorURL = vendorUrlSegments.find( u => u === firstPathSegment );
			const vendorCheck = this.authService?.user?.actorType === 'vendor';
			if ( ( vendorCheck && !checkVendorURL ) || ( !vendorCheck && checkVendorURL ) ) {
				if ( checkVendorURL === 'profile' ) return isActivate = true;
				checkVendorURL = '';
				this.router.navigate( [ '/signin' ] );
				return isActivate = false;
			}
		}


		const checkAdmin = route.data.checkAdmin ? true : false;
		const checkClientAdmin = route.data.checkClientAdmin ? true : false;
		const checkProfileComplete = route.data.checkProfileComplete ? true : false;
		const checkApproved = route.data.checkApproved ? true : false;
		const checkKaizenEmployee = route.data.checkApproved ? true : false;

		const contact = this.ss.currentUser?.contact;
		this.notAllowedOnDocuments = true;
		try {
			const whitelist = await this.wl.getWhitelistByUser();
			this.notAllowedOnDocuments = whitelist.indexOf( contact ) === -1;
		} catch ( error ) {
			console.error( error );
		}
		if ( await this.authorizationService.hasRole( 'cdc' ) ) this.notAllowedOnDocuments = false;
		if ( state.url.indexOf( 'docs-admin' ) !== -1 && this.ss.currentUser && this.notAllowedOnDocuments ) {
			console.log( 'Denied by email' );
			isActivate = false;
		}
		if ( this.ss.currentUser && this.ss.currentUser?.isAdmin !== true && this.authorizationService.isClientAdmin() !== true && checkAdmin ) {
			// Redirect, not just cancel: opened from a URL, a cancelled navigation leaves an empty page
			isActivate = this.router.createUrlTree( [ '/global-view' ] );
		}
		if ( this.ss.currentUser && this.authorizationService.isClientAdmin() !== true && checkClientAdmin ) {
			isActivate = false;
		}
		if ( this.ss.currentUser && this.ss.currentUser?.isApproved !== true && checkProfileComplete ) {
			isActivate = false;
		}
		if ( this.ss.currentUser && this.ss.currentUser?.isApproved !== true && checkApproved ) {
			isActivate = false;
		}
		if ( this.ss.currentUser && this.ss.currentUser?.profileType && checkKaizenEmployee ) {
			if ( this.ss.currentUser?.profileType !== 'Employee' ) isActivate = false;
		}
		if ( actorType !== 'contact' && actorType !== 'vendor' ) {
			const refs = await this.contactReferenceService.getContactReferenceByContactWithCache( this.ss.currentUser?.contact );
			this.epcexternalPartyContact = await this.authorizationService.hasRole( 'epc' ) || await this.authorizationService.hasRole( 'svcr' ) || await this.authorizationService.hasRole( 'sov' );
			this.soaViewerContact = await this.authorizationService.hasRole( 'sov' );
			this.lawyerUser = !!refs?.find( ref => ref.function === 'law' );
			const hasRequestsReadOnly = !this.ss.currentUser?.id?.includes( 'contact-' )
				? !!refs?.find( ref => ref.function === 'requestro' )
				: false;
			const hasVisitorsAccess = !this.ss.currentUser?.id?.includes( 'contact-' )
				? await this.authorizationService.hasRole( 'cvvg' ) || await this.authorizationService.hasRole( 'visitor-security-guard' )
				: false;

			// Lock the app so a user with requestro and/or (cvvg/vsg) only sees those modules.
			if ( hasRequestsReadOnly && hasVisitorsAccess ) {
				const allow = firstPathSegment === 'request-admin' || firstPathSegment === 'visitors';
				if ( allow ) return true;
				return this.router.createUrlTree( ['request-admin'] ); // or ['visitors'] as your default
			} else if ( hasRequestsReadOnly ) {
				if ( firstPathSegment === 'request-admin' ) return true;
				return this.router.createUrlTree( ['request-admin'] );
			}
			
			const checkExternalURL = externalURLs.find( u => u === firstPathSegment );
			if ( ( firstPathSegment !== 'global-view' && this.epcexternalPartyContact && !checkExternalURL ) ) {
				isActivate = this.router.createUrlTree( [ '/global-view/' ] );
			}


			if ( !!this.soaViewerContact && !isSoaAllowedUrl( state.url ) ) {
				isActivate = await this.router.createUrlTree( [ '/global-view/' ] );
			}


			let contactId;
			if ( firstPathSegment === 'collections' ) {
				contactId = route?.children[ 0 ]?.firstChild?.params[ 'contactid' ];
			}
			if ( firstPathSegment !== 'collections' && this.lawyerUser ) {
				isActivate = false;
			}
			if ( firstPathSegment === 'collections' && this.lawyerUser &&
				contactId === 'portfolioDashboard' && contactId === 'dashboard' &&
				contactId === 'priority-list' && contactId === 'monthlyCollection' &&
				contactId === 'totalCollection' && contactId === 'collectionManagement'
			) {
				isActivate = false;
			}
		}
		return isActivate;
	}
}
